import { useState } from 'react';

export interface Arrivals {
  /** Index of the first entry that arrived in the latest batch, or null. */
  arrivedFrom: number | null;
  /** How many arrived in the latest batch; 0 when nothing did. */
  delta: number;
}

/**
 * Which entries of a growing list are new (spec 097 FR-006, FR-015).
 *
 * A batch is "the entries added since the last count", and only a growth from a
 * non-zero count is a batch: the first load of a transcript is not an arrival,
 * it is the transcript. The answer is held in state, adjusted during render
 * when the count changes (React's own pattern for deriving state from props),
 * so the render that shows the new entries is the one that knows which they
 * are — a ref updated in an effect would be one render late.
 */
export function useArrivals(count: number, ready = true): Arrivals {
  /*
    `seen` is null until the first fetch has answered — nothing that render
    shows is news, however many entries it carries. From then on every increase
    is an arrival, including the first entries after "Waiting for the first
    turn…": a reader who watched an empty run fill up is exactly the reader the
    cue is for, and that count went from a real zero, not from "not loaded yet".
  */
  const [seen, setSeen] = useState<number | null>(ready ? count : null);
  const [arrivals, setArrivals] = useState<Arrivals>({
    arrivedFrom: null,
    delta: 0,
  });
  if (!ready) return arrivals;
  if (seen === null) {
    setSeen(count);
    return arrivals;
  }
  if (count !== seen) {
    setSeen(count);
    setArrivals(
      count > seen
        ? { arrivedFrom: seen, delta: count - seen }
        : { arrivedFrom: null, delta: 0 },
    );
  }
  return arrivals;
}
